import { Component } from "@theme/component";
import { StandardEvents, CartLinesUpdateEvent } from "@shopify/events";
import { DrawerOpenEvent } from "@theme/theme-drawer";
import { sectionRenderer } from "@theme/section-renderer";
import { formatMoney } from "@theme/money-formatting";
import { RecentlyViewed } from "@theme/recently-viewed-products";
import { syncFreeShippingDiscount } from "@theme/free-shipping-checkout";

const CART_DRAWER_SECTION_ID = "cart-drawer-section";
const RECENTLY_VIEWED_LIMIT = 4;
const ADDON_POPUP_CONTENT_SELECTOR = ".lb-addon-popup-content";
const ADDON_POPUP_HOST_SELECTOR = "lb-addon-popup, .lb-addon-popup";

/** @type {{ key: string, html: string } | null} */
let recentlyViewedMarkupCache = null;

/** @type {MutationObserver | null} */
let addonPopupObserver = null;

/** @type {MutationObserver | null} */
let addonPopupHostObserver = null;

/** @type {WeakSet<HTMLElement>} */
const watchedAddonPopupHosts = new WeakSet();

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #upsellCheckoutInProgress = false;

  /** @type {AbortController | null} */
  #recentlyViewedAbort = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest("theme-drawer")
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest("dialog");
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate,
    );
    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen,
    );
    this.addEventListener("click", this.#handleRecommendationClick);
    this.addEventListener("click", this.#handleCheckoutClick, true);
    this.#setupRecommendations();
    this.#setupUpsellVariants();
    this.#setupRecentlyViewed();
    observeLimeSpotAddonPopups();
    syncFreeShippingDiscount();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute("open")) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate,
    );
    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen,
    );
    this.removeEventListener("click", this.#handleRecommendationClick);
    this.removeEventListener("click", this.#handleCheckoutClick, true);
    this.#recentlyViewedAbort?.abort();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#setupRecommendations();
    this.#setupUpsellVariants();
    this.#setupRecentlyViewed();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const cta = this.querySelector(
        "shopify-payment-terms",
      )?.shadowRoot?.querySelector("#shopify-installments-cta");
      cta?.addEventListener("click", () => this.#themeDrawer?.close(), {
        once: true,
      });
    });
  };

  #handleRecommendationClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest("[data-supra-cart-tab]");
    if (tab instanceof HTMLButtonElement) {
      this.#selectEmptyTab(tab.dataset.supraCartTab || "bag");
      return;
    }

    const button = event.target.closest("[data-supra-cart-scroll]");
    if (!(button instanceof HTMLButtonElement)) return;

    const track = this.querySelector("[data-supra-cart-track]");
    if (!(track instanceof HTMLElement)) return;

    const direction = button.dataset.supraCartScroll === "previous" ? -1 : 1;
    track.scrollBy({
      left: direction * track.clientWidth * 0.8,
      behavior: "smooth",
    });
  };

  #selectEmptyTab(selected) {
    this.querySelectorAll("[data-supra-cart-tab]").forEach((tab) => {
      const active =
        tab instanceof HTMLElement && tab.dataset.supraCartTab === selected;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", String(active));
    });
    this.querySelectorAll("[data-supra-cart-panel]").forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.hidden = panel.dataset.supraCartPanel !== selected;
    });

    if (selected === "recent") {
      this.#setupRecentlyViewed();
    }
  }

  /**
   * Loads products the visitor actually viewed into every recently viewed grid.
   * @returns {Promise<void>}
   */
  async #setupRecentlyViewed() {
    const roots = this.#getRecentlyViewedRoots();
    if (roots.length === 0) return;

    const ids = RecentlyViewed.getProducts().slice(0, RECENTLY_VIEWED_LIMIT);
    if (ids.length === 0) {
      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        this.#setRecentlyViewedBusy(roots, false);
        return;
      }

      this.#renderRecentlyViewedEmpty(roots);
      return;
    }

    const cacheKey = ids.join(",");
    if (recentlyViewedMarkupCache?.key === cacheKey) {
      this.#writeRecentlyViewedGrid(roots, recentlyViewedMarkupCache.html);
      return;
    }

    this.#setRecentlyViewedBusy(roots, true);

    const routes = typeof Theme !== "undefined" ? Theme.routes : {};
    const searchUrl = routes.search_url || "/search";
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set("q", ids.map((id) => `id:${id}`).join(" OR "));
    url.searchParams.set("type", "product");

    this.#recentlyViewedAbort?.abort();
    this.#recentlyViewedAbort = new AbortController();

    try {
      const html = await sectionRenderer.getSectionHTML(
        CART_DRAWER_SECTION_ID,
        false,
        url,
        this.#recentlyViewedAbort.signal,
      );
      const markup = this.#extractRecentlyViewedMarkup(html, ids);
      recentlyViewedMarkupCache = { key: cacheKey, html: markup };
      this.#writeRecentlyViewedGrid(roots, markup);
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.warn("[cart-drawer] Recently viewed fetch failed:", error);
      this.#setRecentlyViewedBusy(roots, false);
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getRecentlyViewedRoots() {
    const roots = [];

    for (const root of this.querySelectorAll("[data-supra-cart-recent]")) {
      if (root instanceof HTMLElement) roots.push(root);
    }

    const template = this.querySelector("#empty-cart-template");
    if (template instanceof HTMLTemplateElement) {
      for (const root of template.content.querySelectorAll(
        "[data-supra-cart-recent]",
      )) {
        if (root instanceof HTMLElement) roots.push(root);
      }
    }

    return roots;
  }

  /**
   * @param {string} html
   * @param {string[]} ids
   * @returns {string}
   */
  #extractRecentlyViewedMarkup(html, ids) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const sourceGrid = parsed.querySelector("[data-supra-cart-recent-grid]");
    if (!(sourceGrid instanceof HTMLElement)) return "";

    const order = ids.map(String);
    const cards = [...sourceGrid.querySelectorAll("[data-product-id]")].sort(
      (first, second) =>
        order.indexOf(first.getAttribute("data-product-id") || "") -
        order.indexOf(second.getAttribute("data-product-id") || ""),
    );

    sourceGrid.replaceChildren(...cards);
    return sourceGrid.innerHTML.trim();
  }

  /**
   * @param {HTMLElement[]} roots
   * @param {string} markup
   */
  #writeRecentlyViewedGrid(roots, markup) {
    if (!markup) {
      this.#renderRecentlyViewedEmpty(roots);
      return;
    }

    for (const root of roots) {
      const grid = root.querySelector("[data-supra-cart-recent-grid]");
      const empty = root.querySelector("[data-supra-cart-recent-empty]");
      if (!(grid instanceof HTMLElement)) continue;

      grid.innerHTML = markup;
      grid.hidden = false;
      if (empty instanceof HTMLElement) empty.hidden = true;
      root.setAttribute("aria-busy", "false");
    }
  }

  /**
   * @param {HTMLElement[]} roots
   */
  #renderRecentlyViewedEmpty(roots) {
    for (const root of roots) {
      const grid = root.querySelector("[data-supra-cart-recent-grid]");
      const empty = root.querySelector("[data-supra-cart-recent-empty]");
      if (grid instanceof HTMLElement) {
        grid.replaceChildren();
        grid.hidden = true;
      }
      if (empty instanceof HTMLElement) empty.hidden = false;
      root.setAttribute("aria-busy", "false");
    }
  }

  /**
   * @param {HTMLElement[]} roots
   * @param {boolean} busy
   */
  #setRecentlyViewedBusy(roots, busy) {
    for (const root of roots) {
      root.setAttribute("aria-busy", String(busy));
    }
  }

  #setupRecommendations() {
    const track = this.querySelector("[data-supra-cart-track]");
    if (!(track instanceof HTMLElement) || track.dataset.supraCartInitialized)
      return;

    track.dataset.supraCartInitialized = "true";
    const update = () => {
      const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
      const progress =
        maxScroll === 0 ? 1 : 0.35 + (track.scrollLeft / maxScroll) * 0.65;
      this.querySelector("[data-supra-cart-progress]")?.style.setProperty(
        "--supra-cart-progress",
        String(progress),
      );
    };
    track.addEventListener("scroll", update, { passive: true });
    update();
  }

  #setupUpsellVariants() {
    const upsell = this.querySelector("[data-supra-cart-upsell]");
    if (!(upsell instanceof HTMLElement)) return;

    const moneyFormat = upsell.dataset.moneyFormat || "";
    const currency = upsell.dataset.currency || "";

    this.querySelectorAll("[data-supra-cart-upsell-item]").forEach((item) => {
      if (!(item instanceof HTMLElement) || item.dataset.supraUpsellInitialized)
        return;

      item.dataset.supraUpsellInitialized = "true";

      const variantsScript = item.querySelector(
        "[data-supra-cart-upsell-variants]",
      );
      if (!variantsScript) return;

      /** @type {Array<{ id: number, option1: string | null, option2: string | null, option3: string | null, price: number, available: boolean, featured_image?: { src?: string, alt?: string } | null }>} */
      let variants;
      try {
        variants = JSON.parse(variantsScript.textContent || "[]");
      } catch {
        return;
      }

      const selects = item.querySelectorAll("[data-supra-cart-upsell-option]");
      const checkbox = item.querySelector("[data-supra-cart-upsell-checkbox]");
      const priceEl = item.querySelector("[data-supra-cart-upsell-price]");
      const imageEl = item.querySelector("[data-supra-cart-upsell-image] img");

      const updateVariant = () => {
        const options = Array.from(selects).map((select) => select.value);
        const variant =
          selects.length > 0
            ? variants.find(
                (entry) =>
                  (entry.option1 || "") === (options[0] || "") &&
                  (entry.option2 || "") === (options[1] || "") &&
                  (entry.option3 || "") === (options[2] || ""),
              )
            : variants.find((entry) => entry.available) || variants[0];

        if (!variant) return;

        if (checkbox instanceof HTMLInputElement) {
          checkbox.value = String(variant.id);
          checkbox.disabled = !variant.available;
          checkbox.dataset.upsellPriceCents = String(variant.price);
        }

        if (priceEl instanceof HTMLElement && variant.price != null) {
          priceEl.textContent = formatMoney(
            variant.price,
            moneyFormat,
            currency,
          );
        }

        if (
          imageEl instanceof HTMLImageElement &&
          variant.featured_image?.src
        ) {
          const imageUrl = new URL(
            variant.featured_image.src,
            window.location.origin,
          );
          imageUrl.searchParams.set("width", "160");
          imageEl.src = imageUrl.href;
          if (variant.featured_image.alt) {
            imageEl.alt = variant.featured_image.alt;
          }
        }

        this.#updateUpsellTotal();
      };

      for (const select of selects) {
        select.addEventListener("change", updateVariant);
      }

      if (checkbox instanceof HTMLInputElement) {
        checkbox.addEventListener("change", () => this.#updateUpsellTotal());
      }

      updateVariant();
    });

    this.#updateUpsellTotal();
  }

  /**
   * @returns {number}
   */
  #getCheckedUpsellCents() {
    let upsellCents = 0;
    const checkboxes = this.querySelectorAll(
      "[data-supra-cart-upsell-checkbox]:checked",
    );

    for (const checkbox of checkboxes) {
      if (!(checkbox instanceof HTMLInputElement) || checkbox.disabled)
        continue;

      upsellCents += Number(checkbox.dataset.upsellPriceCents) || 0;
    }

    return upsellCents;
  }

  /** Updates the estimated total to include checked upsell items (preview only until checkout). */
  #updateUpsellTotal() {
    const totalEl = this.querySelector("[data-cart-total-cents]");
    if (!(totalEl instanceof HTMLElement)) return;

    const baseCents = Number(totalEl.dataset.cartTotalCents) || 0;
    const moneyFormat = totalEl.dataset.moneyFormat || "";
    const currency = totalEl.dataset.currency || "";
    const projectedCents = baseCents + this.#getCheckedUpsellCents();

    totalEl.textContent = formatMoney(projectedCents, moneyFormat, currency);
    this.#updateShippingProgress(projectedCents, moneyFormat, currency);
  }

  /**
   * Previews free-shipping progress from cart total plus checked upsells.
   * @param {number} projectedCents
   * @param {string} moneyFormat
   * @param {string} currency
   */
  #updateShippingProgress(projectedCents, moneyFormat, currency) {
    const shipping = this.querySelector("[data-supra-cart-shipping]");
    if (!(shipping instanceof HTMLElement)) return;

    const threshold = Number(shipping.dataset.shippingThreshold) || 0;
    if (threshold <= 0) return;

    const remaining = threshold - projectedCents;
    const unlocked = remaining <= 0;
    const progress = Math.min(100, (projectedCents / threshold) * 100);
    const label = shipping.dataset.shippingLabel || "FREE SHIPPING";
    const format = moneyFormat || shipping.dataset.moneyFormat || "";
    const currencyCode = currency || shipping.dataset.currency || "";

    const message = shipping.querySelector(
      "[data-supra-cart-shipping-message]",
    );
    if (message instanceof HTMLElement) {
      const labelEl = document.createElement("span");
      labelEl.className = "supra-cart-shipping__label";
      labelEl.textContent = label;

      if (unlocked) {
        message.replaceChildren(labelEl, " earned");
      } else {
        message.replaceChildren(
          "You are just ",
          formatMoney(remaining, format, currencyCode),
          " away from getting ",
          labelEl,
        );
      }
    }

    const fill = shipping.querySelector("[data-supra-cart-shipping-fill]");
    if (fill instanceof HTMLElement) {
      fill.style.width = `${progress}%`;
    }

    const bar = shipping.querySelector(".supra-cart-shipping__bar");
    let pin = shipping.querySelector("[data-supra-cart-shipping-pin]");

    if (unlocked) {
      pin?.remove();
    } else if (pin instanceof HTMLElement) {
      pin.style.left = `${progress}%`;
    } else if (bar instanceof HTMLElement) {
      const nextPin = document.createElement("i");
      nextPin.setAttribute("data-supra-cart-shipping-pin", "");
      nextPin.style.left = `${progress}%`;
      bar.appendChild(nextPin);
    }

    const goal = shipping.querySelector("[data-supra-cart-shipping-goal]");
    if (goal instanceof HTMLElement) {
      goal.classList.toggle("supra-cart-shipping__goal--complete", unlocked);
    }

    const shippingRow = this.querySelector(".cart-totals__shipping");
    if (shippingRow instanceof HTMLElement) {
      shippingRow.hidden = !unlocked;
    }
  }

  #getCartVariantIds() {
    return new Set(
      Array.from(this.querySelectorAll("[data-variant-id]"))
        .map((element) => element.getAttribute("data-variant-id"))
        .filter(Boolean),
    );
  }

  #getCheckedUpsellLineItems() {
    const cartVariantIds = this.#getCartVariantIds();
    /** @type {Array<{ id: number, quantity: number }>} */
    const items = [];

    this.querySelectorAll("[data-supra-cart-upsell-checkbox]:checked").forEach(
      (checkbox) => {
        if (!(checkbox instanceof HTMLInputElement)) return;
        if (checkbox.disabled || !checkbox.value) return;
        if (cartVariantIds.has(checkbox.value)) return;

        items.push({
          id: Number(checkbox.value),
          quantity: 1,
        });
      },
    );

    return items;
  }

  async #addUpsellItemsToCart(items) {
    const routes = typeof Theme !== "undefined" ? Theme.routes : {};
    const addUrl = routes.cart_add_url || "/cart/add.js";

    const response = await fetch(addUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items }),
    });

    const data = await response.json();
    if (data.status) {
      throw new Error(data.description || data.message || "Add to cart failed");
    }

    const cartResponse = await fetch(`${routes.cart_url || "/cart"}.js`);
    const cart = await cartResponse.json();
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: "add",
        context: "cart-drawer",
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferred.promise,
      }),
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        source: "cart-drawer-upsell",
        itemCount: items.length,
      },
    });
  }

  async #redirectToCheckout() {
    await syncFreeShippingDiscount();
    window.location.assign("/checkout");
  }

  #handleCheckoutClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const checkoutButton = event.target.closest(
      "#checkout.cart__checkout-button",
    );
    if (!(checkoutButton instanceof HTMLButtonElement)) return;
    if (!this.contains(checkoutButton) || checkoutButton.disabled) return;

    // LimeSpot's addon popup is a document overlay. The cart is force-modal
    // (top layer), so close it now — especially on mobile — so the popup can
    // show instead of sitting under the sidecart backdrop.
    closeCartDrawerForAddonPopup();

    const upsellItems = this.#getCheckedUpsellLineItems();
    if (this.#upsellCheckoutInProgress) return;

    if (upsellItems.length === 0) {
      if (!this.dataset.freeShippingDiscount) return;

      checkoutButton.disabled = true;
      event.preventDefault();
      event.stopPropagation();
      await this.#redirectToCheckout();
      return;
    }

    this.#upsellCheckoutInProgress = true;
    checkoutButton.disabled = true;
    event.preventDefault();
    event.stopPropagation();

    try {
      await this.#addUpsellItemsToCart(upsellItems);
      await this.#redirectToCheckout();
    } catch (error) {
      console.warn("[cart-drawer] Upsell add failed:", error);
      this.#upsellCheckoutInProgress = false;
      checkoutButton.disabled = false;
      await this.#redirectToCheckout();
    }
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute("auto-open") &&
      event.action === "add" &&
      !this.#themeDrawer?.isOpen;
    const wasEmptyCartDrawer = this.#isCartEmpty();

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest("dialog:modal")
        : null
    );

    if (shouldAutoOpen && !sourceModal && !wasEmptyCartDrawer) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(async ({ detail }) => {
        if (!detail?.didError) {
          const hasDrawerSection = Boolean(
            detail?.sections?.[CART_DRAWER_SECTION_ID],
          );
          const becameEmpty = !wasEmptyCartDrawer && detail?.itemCount === 0;
          // Empty → filled needs a full section render. Also fall back when the
          // add response omitted section HTML so the drawer never stays stale.
          // Filled → empty is handled in cart-items-component; only settle here.
          if (wasEmptyCartDrawer || (!hasDrawerSection && !becameEmpty)) {
            await this.#renderFreshDrawer();
          }
        }

        const settle = () => {
          const currentComponent = document.querySelector(
            "cart-drawer-component",
          );
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (currentComponent instanceof CartDrawerComponent) {
                currentComponent.#updateStickyState();
                currentComponent.#setupRecommendations();
                currentComponent.#setupUpsellVariants();
                currentComponent.#setupRecentlyViewed();
              } else {
                this.#updateStickyState();
                this.#setupRecommendations();
                this.#setupUpsellVariants();
                this.#setupRecentlyViewed();
              }

              syncFreeShippingDiscount();
            });
          });
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          const currentDrawer =
            /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
              document.querySelector("theme-drawer#cart-drawer")
            );
          if (currentDrawer?.isOpen === false) currentDrawer.open();
          else if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener("close", openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError")
          console.warn("[cart-drawer] Event promise rejected:", error);
      });
  };

  async #renderFreshDrawer() {
    await sectionRenderer.renderSection(CART_DRAWER_SECTION_ID, {
      cache: false,
      mode: "full",
      injectStylesheet: true,
    });
  }

  #isCartEmpty() {
    return Boolean(this.querySelector(".cart-drawer--empty"));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector(".cart-drawer__content");
    const summary = dialog.querySelector(".cart-drawer__summary");

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute("cart-summary-sticky", "false");
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute(
      "cart-summary-sticky",
      ratio > this.#summaryThreshold ? "false" : "true",
    );
  }
}

/**
 * Drops the cart out of the top layer immediately so LimeSpot's addon popup
 * can paint. `force-modal` + `showModal()` otherwise keeps the sidecart
 * overlay above any document z-index, which is most obvious on mobile.
 */
const closeCartDrawerForAddonPopup = () => {
  const drawer = document.querySelector("theme-drawer#cart-drawer");
  if (!(drawer instanceof HTMLElement) || !drawer.hasAttribute("open")) return;

  const panel = drawer.querySelector("dialog");
  if (panel instanceof HTMLDialogElement) {
    panel.classList.add("theme-drawer__dialog-instant");
    drawer.addEventListener(
      "theme-drawer:close",
      () => panel.classList.remove("theme-drawer__dialog-instant"),
      { once: true },
    );
  }

  if (typeof drawer.close === "function") {
    drawer.close();
  }
};

/**
 * @param {HTMLElement} content
 * @returns {HTMLElement}
 */
const resolveAddonPopupHost = (content) => {
  const namedHost = content.closest(ADDON_POPUP_HOST_SELECTOR);

  if (namedHost instanceof HTMLElement) return namedHost;

  let current = content.parentElement;

  while (current && current !== document.body) {
    const position = getComputedStyle(current).position;

    if (position === "fixed") return current;

    current = current.parentElement;
  }

  return content;
};

/**
 * @param {HTMLElement} host
 * @returns {boolean}
 */
const isAddonPopupVisible = (host) => {
  if (!host.isConnected || host.hasAttribute("hidden")) return false;

  const style = getComputedStyle(host);

  if (style.display === "none" || style.visibility === "hidden") return false;

  return host.getBoundingClientRect().height > 0;
};

/**
 * @param {HTMLElement} host
 */
const syncAddonPopupHost = (host) => {
  if (!isAddonPopupVisible(host)) return;

  closeCartDrawerForAddonPopup();
};

/**
 * @param {ParentNode | HTMLElement} [root]
 */
const syncLimeSpotAddonPopups = (root = document) => {
  const hosts = [];

  if (root instanceof HTMLElement && root.matches(ADDON_POPUP_HOST_SELECTOR)) {
    hosts.push(root);
  }

  if (typeof root.querySelectorAll === "function") {
    for (const node of root.querySelectorAll(ADDON_POPUP_HOST_SELECTOR)) {
      if (node instanceof HTMLElement) hosts.push(node);
    }
  }

  if (
    root instanceof HTMLElement &&
    root.matches(ADDON_POPUP_CONTENT_SELECTOR)
  ) {
    hosts.push(resolveAddonPopupHost(root));
  }

  if (typeof root.querySelectorAll === "function") {
    for (const node of root.querySelectorAll(ADDON_POPUP_CONTENT_SELECTOR)) {
      if (node instanceof HTMLElement) {
        hosts.push(resolveAddonPopupHost(node));
      }
    }
  }

  for (const host of hosts) {
    watchAddonPopupHost(host);
    syncAddonPopupHost(host);
  }
};

/**
 * @param {HTMLElement} host
 */
const watchAddonPopupHost = (host) => {
  if (!addonPopupHostObserver || watchedAddonPopupHosts.has(host)) return;

  watchedAddonPopupHosts.add(host);
  addonPopupHostObserver.observe(host, {
    attributes: true,
    attributeFilter: ["style", "class", "hidden"],
  });
};

const observeLimeSpotAddonPopups = () => {
  if (addonPopupObserver) {
    syncLimeSpotAddonPopups();
    return;
  }

  addonPopupHostObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target instanceof HTMLElement) {
        syncAddonPopupHost(mutation.target);
      }
    }
  });

  addonPopupObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLElement) syncLimeSpotAddonPopups(node);
      }
    }
  });

  addonPopupObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  syncLimeSpotAddonPopups();
};

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
